import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { CalendarDays, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { PRESETS, computePreset, matchPreset, rangeLabel } from "@/lib/dateRanges";

const toDate = (s) => (s ? new Date(s + "T00:00:00") : undefined);
const toStr = (d) => (d ? format(d, "yyyy-MM-dd") : "");

// Reusable Nirvana date-range picker with quick-range presets + custom range.
// Emits inclusive "YYYY-MM-DD" strings via onChange(start, end). From/To
// validation is preserved (each calendar disables the invalid side).
export function DateRangePicker({ start, end, onChange, align = "start", className, testid = "date-range" }) {
  const [open, setOpen] = useState(false);
  const active = matchPreset(start, end);
  const [showCustom, setShowCustom] = useState(active === "custom");

  const pick = (key) => {
    if (key === "custom") { setShowCustom(true); return; }
    if (key === "clear") { onChange("", ""); setShowCustom(false); setOpen(false); return; }
    const r = computePreset(key);
    onChange(r.start, r.end);
    setShowCustom(false);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) setShowCustom(matchPreset(start, end) === "custom"); }}>
      <PopoverTrigger asChild>
        <Button variant="outline" data-testid={`${testid}-trigger`} data-test-wrapper={testid}
          className={cn("min-w-[190px] justify-start gap-2 font-normal", (!start && !end) && "text-muted-foreground", className)}>
          <CalendarDays className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="truncate">{rangeLabel(start, end)}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align={align} className="w-auto p-0" data-testid={`${testid}-popover`}>
        <div className="flex flex-col sm:flex-row">
          <div className="flex min-w-[168px] flex-col gap-0.5 border-b border-border p-2 sm:border-b-0 sm:border-r">
            {PRESETS.map((p) => (
              <button key={p.key} onClick={() => pick(p.key)} data-testid={`${testid}-preset-${p.key}`}
                className={cn("flex items-center justify-between rounded-md px-3 py-1.5 text-left text-sm transition-colors hover:bg-muted",
                  active === p.key && "bg-accent-subtle font-medium text-foreground")}>
                {p.label} {active === p.key && <Check className="h-3.5 w-3.5 text-accent" />}
              </button>
            ))}
            <button onClick={() => pick("custom")} data-testid={`${testid}-custom`}
              className={cn("rounded-md px-3 py-1.5 text-left text-sm transition-colors hover:bg-muted",
                (showCustom || active === "custom") && "bg-accent-subtle font-medium text-foreground")}>
              Custom range
            </button>
            <div className="my-1 h-px bg-border" />
            <button onClick={() => pick("clear")} data-testid={`${testid}-clear`}
              className="rounded-md px-3 py-1.5 text-left text-sm text-muted-foreground transition-colors hover:bg-muted">
              Clear
            </button>
          </div>
          {showCustom && (
            <div className="flex flex-col gap-3 p-3 lg:flex-row">
              <div data-testid={`${testid}-from`}>
                <div className="mb-1 px-1 text-xs font-medium text-muted-foreground">From</div>
                <Calendar mode="single" selected={toDate(start)} defaultMonth={toDate(start) || toDate(end)}
                  disabled={(d) => end && d > toDate(end)}
                  onSelect={(d) => onChange(toStr(d), end)} />
              </div>
              <div data-testid={`${testid}-to`}>
                <div className="mb-1 px-1 text-xs font-medium text-muted-foreground">To</div>
                <Calendar mode="single" selected={toDate(end)} defaultMonth={toDate(end) || toDate(start)}
                  disabled={(d) => start && d < toDate(start)}
                  onSelect={(d) => onChange(start, toStr(d))} />
              </div>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
